import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getInvoice, createInvoice, getClients, getServices, getInvoicePdf, getInvoicePayments, getInvoiceDocuments, getInvoiceDocumentTypes, uploadInvoiceDocument, updateInvoiceDocument, deleteInvoiceDocument, downloadInvoiceDocument } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Save, FileText, Plus, Trash2, FileDown, Printer, CreditCard, Wallet, Banknote, Building2, Upload, Paperclip, Download, Edit, X, File, Image, FileType } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getInvoiceStatusClass } from '@/lib/utils';
import { API_BASE } from '@/lib/api';

const paymentTerms = ['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 60'];

// Payment method icons
const PaymentMethodIcon = ({ method }) => {
  switch (method) {
    case 'Wallet':
      return <Wallet className="w-4 h-4 text-primary" />;
    case 'Card':
      return <CreditCard className="w-4 h-4 text-blue-500" />;
    case 'Bank Transfer':
      return <Building2 className="w-4 h-4 text-green-600" />;
    case 'Cheque':
      return <FileText className="w-4 h-4 text-amber-500" />;
    default:
      return <Banknote className="w-4 h-4 text-emerald-500" />;
  }
};

const paymentMethodColors = {
  'Wallet': 'bg-primary/10 text-primary border-primary/30',
  'Card': 'bg-blue-50 text-blue-700 border-blue-200',
  'Bank Transfer': 'bg-green-50 text-green-700 border-green-200',
  'Cheque': 'bg-amber-50 text-amber-700 border-amber-200',
  'Cash': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Online': 'bg-purple-50 text-purple-700 border-purple-200'
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [invoice, setInvoice] = useState({
    client_id: '',
    invoice_type: 'Invoice',
    payment_terms: 'Due on Receipt',
    items: [{ description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, vat_applicable: true }],
    discount_type: 'None',
    discount_value: 0,
    vat_percentage: 5,
    notes: '',
    terms_conditions: ''
  });
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Document upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [newDocument, setNewDocument] = useState({
    document_name: '',
    document_type: 'Other',
    notes: ''
  });
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadData();
  }, [id, isNew]);

  const loadData = async () => {
    try {
      const [clientsRes, servicesRes, docTypesRes] = await Promise.all([
        getClients({ limit: 1000 }),
        getServices({ limit: 200 }),
        getInvoiceDocumentTypes().catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data.items);
      setServices(servicesRes.data.items);
      setDocumentTypes(docTypesRes.data || []);

      if (!isNew && id) {
        setLoading(true);
        const [invoiceRes, paymentsRes, docsRes] = await Promise.all([
          getInvoice(id),
          getInvoicePayments(id).catch(() => ({ data: { payment_history: [] } })),
          getInvoiceDocuments(id).catch(() => ({ data: [] }))
        ]);
        setInvoice(invoiceRes.data);
        // Extract payment_history from the response
        const paymentData = paymentsRes.data?.payment_history || paymentsRes.data || [];
        setPayments(Array.isArray(paymentData) ? paymentData : []);
        setDocuments(docsRes.data || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setInvoice((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setInvoice((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setInvoice((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, vat_applicable: true }]
    }));
  };

  const removeItem = (index) => {
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const selectService = (index, serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      handleItemChange(index, 'description', service.name);
      handleItemChange(index, 'unit_price', service.base_price || 0);
      handleItemChange(index, 'govt_fee', service.govt_fee || 0);
      handleItemChange(index, 'service_fee', service.service_fee || 0);
    }
  };

  const calculateLineTotal = (item) => {
    const qty = item.quantity || 1;
    return (item.unit_price * qty) + (item.govt_fee || 0) + (item.service_fee || 0);
  };

  const calculateTotals = () => {
    const subtotal = invoice.items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
    let discount = 0;
    if (invoice.discount_type === 'Percentage') {
      discount = subtotal * (invoice.discount_value / 100);
    } else if (invoice.discount_type === 'Fixed') {
      discount = invoice.discount_value;
    }
    const afterDiscount = subtotal - discount;
    const vatableAmount = invoice.items.reduce((sum, item) => {
      if (item.vat_applicable !== false) {
        return sum + calculateLineTotal(item);
      }
      return sum;
    }, 0) - discount;
    const vat = vatableAmount * (invoice.vat_percentage / 100);
    const total = afterDiscount + vat;
    return { subtotal, discount, vat, total };
  };

  // Document handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!newDocument.document_name) {
        setNewDocument(prev => ({ ...prev, document_name: file.name }));
      }
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_name', newDocument.document_name || selectedFile.name);
      formData.append('document_type', newDocument.document_type);
      formData.append('notes', newDocument.notes || '');
      
      await uploadInvoiceDocument(id, formData);
      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setSelectedFile(null);
      setNewDocument({ document_name: '', document_type: 'Other', notes: '' });
      // Reload documents
      const docsRes = await getInvoiceDocuments(id);
      setDocuments(docsRes.data || []);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocument({
      id: doc.id,
      document_name: doc.document_name,
      document_type: doc.document_type,
      notes: doc.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveDocument = async () => {
    try {
      await updateInvoiceDocument(id, editingDocument.id, {
        document_name: editingDocument.document_name,
        document_type: editingDocument.document_type,
        notes: editingDocument.notes
      });
      toast.success('Document updated');
      setShowEditDialog(false);
      setEditingDocument(null);
      // Reload documents
      const docsRes = await getInvoiceDocuments(id);
      setDocuments(docsRes.data || []);
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await deleteInvoiceDocument(id, docId);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await downloadInvoiceDocument(id, doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || doc.document_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (mimeType?.includes('pdf')) return <FileText className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocumentTypeBadgeColor = (type) => {
    const colors = {
      'Government Fee Receipt': 'bg-blue-100 text-blue-800',
      'Payment Receipt': 'bg-green-100 text-green-800',
      'Client Document': 'bg-purple-100 text-purple-800',
      'ID Copy': 'bg-amber-100 text-amber-800',
      'Passport Copy': 'bg-red-100 text-red-800',
      'Contract': 'bg-indigo-100 text-indigo-800',
      'Authorization Letter': 'bg-cyan-100 text-cyan-800',
      'Photo': 'bg-pink-100 text-pink-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors['Other'];
  };

  const handleSave = async () => {
    if (!invoice.client_id) {
      toast.error('Please select a client');
      return;
    }

    setSaving(true);
    try {
      const totals = calculateTotals();
      const payload = {
        ...invoice,
        subtotal: totals.subtotal,
        discount_amount: totals.discount,
        vat_amount: totals.vat,
        total_amount: totals.total,
        balance_due: totals.total,
        items: invoice.items.map(item => ({
          ...item,
          line_total: calculateLineTotal(item)
        }))
      };

      await createInvoice(payload);
      toast.success('Invoice created');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await getInvoicePdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // New invoice form
  if (isNew) {
    return (
      <div className="space-y-6" data-testid="invoice-form">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold">New Invoice</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client & Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Client *</Label>
                    <Select value={invoice.client_id} onValueChange={(v) => handleChange('client_id', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.trade_name || c.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Select value={invoice.payment_terms} onValueChange={(v) => handleChange('payment_terms', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTerms.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Line Items</CardTitle>
                <Button size="sm" variant="outline" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {invoice.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <Label>Description</Label>
                        <Select onValueChange={(v) => selectService(idx, v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select service or type" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          className="mt-1"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          placeholder="Or type description"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label>Qty</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', parseInt(e.target.value) || 1)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Unit Price</Label>
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Govt Fee</Label>
                        <Input
                          type="number"
                          value={item.govt_fee}
                          onChange={(e) => handleItemChange(idx, 'govt_fee', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Service Fee</Label>
                        <Input
                          type="number"
                          value={item.service_fee}
                          onChange={(e) => handleItemChange(idx, 'service_fee', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-1">
                        <Button variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Additional Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select value={invoice.discount_type} onValueChange={(v) => handleChange('discount_type', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None</SelectItem>
                        <SelectItem value="Percentage">Percentage</SelectItem>
                        <SelectItem value="Fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {invoice.discount_type !== 'None' && (
                    <div>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        value={invoice.discount_value}
                        onChange={(e) => handleChange('discount_value', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Label>Notes</Label>
                  <Textarea
                    value={invoice.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">Invoice Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="font-mono">-{formatCurrency(totals.discount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">VAT ({invoice.vat_percentage}%)</span>
                    <span className="font-mono">{formatCurrency(totals.vat)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-mono font-semibold text-lg">{formatCurrency(totals.total)}</span>
                  </div>
                </div>
                <Button className="w-full mt-4" onClick={handleSave} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Create Invoice'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // View existing invoice
  return (
    <div className="space-y-6" data-testid="invoice-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">{invoice.invoice_number}</h1>
              <Badge className={getInvoiceStatusClass(invoice.status)}>{invoice.status}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">Created {formatDate(invoice.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadPdf}>
            <FileDown className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/invoices/${id}/print`, '_blank')}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          {invoice.status !== 'Paid' && (
            <Button onClick={() => navigate(`/payments?invoice_id=${id}`)}>
              Record Payment
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Client</p>
                  <p className="font-medium">{invoice.client?.display_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">TRN</p>
                  <p className="font-mono">{invoice.client?.trn || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Contact</p>
                  <p>{invoice.client?.contact_person || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p>{invoice.client?.contact_phone || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">#</th>
                      <th className="text-left py-2">Description</th>
                      <th className="text-center py-2">Qty</th>
                      <th className="text-right py-2">Unit Price</th>
                      <th className="text-right py-2">Govt Fee</th>
                      <th className="text-right py-2">Service Fee</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-border/50">
                        <td className="py-2">{idx + 1}</td>
                        <td className="py-2">{item.description}</td>
                        <td className="py-2 text-center">{item.quantity}</td>
                        <td className="py-2 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                        <td className="py-2 text-right font-mono">{formatCurrency(item.govt_fee || 0)}</td>
                        <td className="py-2 text-right font-mono">{formatCurrency(item.service_fee || 0)}</td>
                        <td className="py-2 text-right font-mono font-semibold">{formatCurrency(item.line_total || item.total || (item.quantity * item.unit_price))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Payment History Section */}
          {payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Receipt #</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment, idx) => {
                      const method = payment.method || payment.payment_method;
                      return (
                        <TableRow key={payment.id || idx} data-testid={`payment-row-${payment.id || idx}`}>
                          <TableCell>{formatDate(payment.date || payment.payment_date)}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {payment.payment_number || payment.receipt_number}
                            {payment.is_split_payment && (
                              <Badge variant="outline" className="ml-2 text-xs">Split</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <PaymentMethodIcon method={method} />
                              <Badge 
                                variant="outline" 
                                className={paymentMethodColors[method] || 'bg-gray-50 text-gray-700'}
                              >
                                {method}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {payment.reference || payment.reference_number || '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-green-600">
                            {formatCurrency(payment.amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Payments</span>
                  <span className="font-mono font-semibold text-green-600">
                    {formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0))}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents Section */}
          {!isNew && (
            <Card data-testid="documents-section">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Documents ({documents.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => setShowUploadDialog(true)} data-testid="upload-document-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Paperclip className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No documents attached</p>
                    <p className="text-sm mt-1">Click upload to add documents</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border"
                        data-testid={`document-item-${doc.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="p-2 bg-background rounded-lg border">
                            {getFileIcon(doc.mime_type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{doc.document_name}</span>
                              <Badge className={`text-xs ${getDocumentTypeBadgeColor(doc.document_type)}`}>
                                {doc.document_type}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <span>{doc.file_name}</span>
                              <span>•</span>
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>•</span>
                              <span>{formatDate(doc.created_at)}</span>
                            </div>
                            {doc.notes && (
                              <p className="text-xs text-muted-foreground mt-1 truncate">{doc.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDownloadDocument(doc)}
                            title="Download"
                            data-testid={`download-doc-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEditDocument(doc)}
                            title="Edit"
                            data-testid={`edit-doc-${doc.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-destructive hover:text-destructive"
                            title="Delete"
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice Date</span>
                  <span>{formatDate(invoice.invoice_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Due Date</span>
                  <span>{formatDate(invoice.due_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Payment Terms</span>
                  <span>{invoice.payment_terms || '-'}</span>
                </div>
                <div className="border-t pt-3 flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className="font-mono">-{formatCurrency(invoice.discount_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT ({invoice.vat_percentage}%)</span>
                  <span className="font-mono">{formatCurrency(invoice.vat_amount)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(invoice.total_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Paid</span>
                  <span className="font-mono text-green-600">{formatCurrency(invoice.amount_paid)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold">Balance Due</span>
                  <span className={`font-mono font-semibold text-lg ${invoice.balance_due > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {formatCurrency(invoice.balance_due)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Attach a document to invoice {invoice.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>File</Label>
              <div className="flex items-center gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="flex-1"
                  data-testid="file-input"
                />
              </div>
              {selectedFile && (
                <p className="text-xs text-muted-foreground">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Document Name</Label>
              <Input
                value={newDocument.document_name}
                onChange={(e) => setNewDocument(prev => ({ ...prev, document_name: e.target.value }))}
                placeholder="Enter document name"
                data-testid="document-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select
                value={newDocument.document_type}
                onValueChange={(v) => setNewDocument(prev => ({ ...prev, document_type: v }))}
              >
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={newDocument.notes}
                onChange={(e) => setNewDocument(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about this document..."
                rows={2}
                data-testid="document-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} disabled={uploadingDoc || !selectedFile} data-testid="upload-submit-btn">
              {uploadingDoc ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update document details
            </DialogDescription>
          </DialogHeader>
          {editingDocument && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Document Name</Label>
                <Input
                  value={editingDocument.document_name}
                  onChange={(e) => setEditingDocument(prev => ({ ...prev, document_name: e.target.value }))}
                  data-testid="edit-document-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select
                  value={editingDocument.document_type}
                  onValueChange={(v) => setEditingDocument(prev => ({ ...prev, document_type: v }))}
                >
                  <SelectTrigger data-testid="edit-document-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingDocument.notes}
                  onChange={(e) => setEditingDocument(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add notes..."
                  rows={2}
                  data-testid="edit-document-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDocument} data-testid="save-document-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
